import { ShopSystemConfiguration } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'
import {
  deleteConfigurator,
  fetchAllSavedConfigurators,
  getSingleConfigurator,
  saveConfigurator,
  updateConfigurator,
} from '../api/shop-saved-system-configurator.api'
import { FavoriteSystemResponse, SaveConfiguratorResponse } from '../models'
import { useShopFetchWithResult } from './use-shop-fetch'
import { useState } from 'react'
import { FavoriteConfiguratorDto } from '../api/dto/save-configurator.dto'

interface UseShopSavedSystemConfiguration {
  getAllSavedSystemConfigurations: () => Promise<FavoriteSystemResponse | null>
  saveSystemConfiguration: (configurator: ShopSystemConfiguration) => Promise<SaveConfiguratorResponse | null>
  deleteSystemConfiguration: (configuratorId: string) => Promise<SaveConfiguratorResponse | null>
  getSystemConfiguration: (configuratorId: string) => Promise<FavoriteConfiguratorDto | null>
  updateSystemConfiguration: (configuratorId: string, configurator: ShopSystemConfiguration) => Promise<SaveConfiguratorResponse | null>
  isLoading: boolean
}

/*
 * This hook hold all the endpoints that we can use for Favorite System Configurator,
 * like getting / saving / removing / update configurator
 */
const useShopSavedSystemConfiguration = (): UseShopSavedSystemConfiguration => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const shopFetch = useShopFetchWithResult()
  const getAllSavedSystemConfigurations = async (): Promise<FavoriteSystemResponse | null> => {
    try {
      const response = await fetchAllSavedConfigurators(shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    }
  }
  const saveSystemConfiguration = async (configurator: ShopSystemConfiguration): Promise<SaveConfiguratorResponse | null> => {
    try {
      setIsLoading(true)
      const response = await saveConfigurator(configurator, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }
  const deleteSystemConfiguration = async (configuratorId: string): Promise<SaveConfiguratorResponse | null> => {
    try {
      setIsLoading(true)
      const response = await deleteConfigurator(configuratorId, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }
  const updateSystemConfiguration = async (
    configuratorId: string,
    configurator: ShopSystemConfiguration
  ): Promise<SaveConfiguratorResponse | null> => {
    try {
      setIsLoading(true)
      const response = await updateConfigurator(configuratorId, configurator, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }
  const getSystemConfiguration = async (configuratorId: string): Promise<FavoriteConfiguratorDto | null> => {
    try {
      setIsLoading(true)
      const response = await getSingleConfigurator(configuratorId, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getAllSavedSystemConfigurations,
    saveSystemConfiguration,
    deleteSystemConfiguration,
    getSystemConfiguration,
    updateSystemConfiguration,
    isLoading,
  }
}

export default useShopSavedSystemConfiguration
